import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import api from '@/api/axios';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CustomPageView = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/pages/slug/${slug}`);
      setPage(response.data);
    } catch (error) {
      console.error('Failed to fetch page:', error);
      toast.error('Page not found');
      setTimeout(() => navigate('/'), 2000);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-12">
          <Skeleton className="h-12 w-1/3 mb-8" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  if (!page) {
    return null;
  }

  return (
    <div className="min-h-screen bg-secondary-50" data-testid="custom-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{page.title} | Noble Shield</title>
        {page.meta_description && <meta name="description" content={page.meta_description} />}
        {page.meta_keywords && <meta name="keywords" content={page.meta_keywords} />}
        {page.robots && <meta name="robots" content={page.robots} />}
        {page.canonical_url && <link rel="canonical" href={page.canonical_url} />}
        
        {/* Open Graph Tags */}
        {page.og_title && <meta property="og:title" content={page.og_title} />}
        {page.og_description && <meta property="og:description" content={page.og_description} />}
        {page.og_image && <meta property="og:image" content={page.og_image} />}
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-12">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate(-1)} 
          className="mb-6"
          data-testid="back-button"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card>
          <CardContent className="p-8 md:p-12">
            <h1 className="text-4xl font-bold font-heading text-secondary-900 mb-6">
              {page.title}
            </h1>
            
            <div 
              className="prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-secondary-900
                prose-p:text-secondary-700 prose-p:leading-relaxed
                prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline
                prose-strong:text-secondary-900 prose-strong:font-semibold
                prose-ul:text-secondary-700 prose-ol:text-secondary-700
                prose-li:text-secondary-700
                prose-h1:text-4xl prose-h2:text-3xl prose-h3:text-2xl
                prose-h1:mb-6 prose-h2:mb-4 prose-h3:mb-3
                prose-p:mb-4 prose-ul:mb-4 prose-ol:mb-4"
              dangerouslySetInnerHTML={{ __html: page.content }}
              data-testid="page-content"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomPageView;
